import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, CalendarCheck, MapPin, Mail, Bell, CreditCard, ArrowRight, CheckCircle2, Scissors, TreePine, Leaf, Trash2, Flame, Armchair, Wrench, Sprout, Wind } from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { Reviews } from "@/components/Reviews";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const HERO_IMG = "https://images.pexels.com/photos/35729175/pexels-photo-35729175.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const SERVICE_IMAGES = {
  mowing: "https://images.unsplash.com/photo-1734303023491-db8037a21f09?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxsYXduJTIwbW93aW5nJTIwc2VydmljZXxlbnwwfHx8fDE3ODczMDQ2NzF8MA&ixlib=rb-4.1.0&q=85",
  "hedge-trimming": "https://images.pexels.com/photos/24595772/pexels-photo-24595772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "tree-trimming": "https://images.unsplash.com/photo-1754322449185-31f56117ed87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHx0cmVlJTIwdHJpbW1pbmclMjByZW1vdmFsfGVufDB8fHx8MTc4NzMwNDY3MXww&ixlib=rb-4.1.0&q=85",
  "yard-maintenance": "https://images.unsplash.com/photo-1724556295135-ff92b9aa0a55?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwxfHxsYW5kc2NhcGluZyUyMHlhcmQlMjBjYXJlfGVufDB8fHx8MTc4NzMwNDY3MXww&ixlib=rb-4.1.0&q=85",
};

const SERVICE_ICONS = {
  mowing: Scissors,
  "whipper-snipping": Wind,
  "hedge-trimming": Scissors,
  "tree-trimming": TreePine,
  "leaf-cleanup": Leaf,
  mulching: Sprout,
  "yard-maintenance": Sprout,
  "rubbish-removal": Trash2,
  firewood: Flame,
  "furniture-moving": Armchair,
  "odd-jobs": Wrench,
};

const STEPS = [
  { icon: CalendarCheck, title: "Book online in 2 minutes", text: "Pick your service, date and time. Add photos so we know exactly what you need." },
  { icon: CreditCard, title: "Easy bank transfer", text: "Pay a deposit or the full amount by bank transfer. No card details needed." },
  { icon: Bell, title: "Confirmations & reminders", text: "Get instant confirmation and a reminder the day before we arrive." },
  { icon: MapPin, title: "We show up & get it done", text: "Track your job status from booked to completed. Local, reliable, Cooma-based." },
];

export default function Landing() {
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch(() => {});
    api.get("/config/public").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const phoneDisplay = config?.phone_display || "0455 530 779";
  const phoneTel = config?.phone_tel || "+61455530779";

  return (
    <div className="noise-overlay bg-background min-h-screen" data-testid="landing-page">
      <PublicHeader phoneDisplay={phoneDisplay} phoneTel={phoneTel} />

      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Cooma region landscape" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-28 sm:py-40">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-white/80 mb-4 animate-fade-up">
            Blake&apos;s Mowing &amp; Odd Jobs &middot; Cooma &middot; Bredbo &middot; Berridale
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-white max-w-2xl animate-fade-up" style={{ animationDelay: "0.1s" }}>
            Your yard, sorted. Booked in minutes.
          </h1>
          <p className="text-white/85 text-base sm:text-lg mt-5 max-w-xl leading-relaxed animate-fade-up" style={{ animationDelay: "0.2s" }}>
            I&apos;ll keep your lawns at their best — mowing, hedges, tree work, clean-ups and odd jobs across
            Cooma, Bredbo, Berridale and the Snowy Monaro. Book online, pay by bank transfer, and it&apos;s sorted.
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 mt-8 animate-fade-up" style={{ animationDelay: "0.3s" }}>
            <Link to="/book" data-testid="hero-book-button" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto rounded-full bg-primary hover:bg-[#21421D] text-white px-8 text-base hover:-translate-y-0.5 transition-transform duration-200 min-h-[52px]">
                Book a Job Online <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <a href={`tel:${phoneTel}`} data-testid="hero-call-button" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto rounded-full bg-accent hover:bg-[#B85827] text-white px-8 text-base animate-pulse-ring transition-colors min-h-[52px]">
                <Phone className="mr-2 h-5 w-5" /> Call {phoneDisplay}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section id="services" className="py-24 sm:py-32" data-testid="services-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">What we do</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-3">Services built around your yard</h2>
          <p className="text-muted-foreground max-w-xl mb-12">Every job starts with a clear estimate. Book any service online and we will confirm the details.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => {
              const img = SERVICE_IMAGES[s.id];
              const Icon = SERVICE_ICONS[s.id] || Wrench;
              return (
                <Link key={s.id} to={`/book?service=${s.id}`} data-testid={`service-card-${s.id}`}
                  className="group bg-white rounded-2xl border border-border overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-200 animate-fade-up"
                  style={{ animationDelay: `${Math.min(i * 0.05, 0.4)}s` }}>
                  {img && (
                    <div className="h-44 overflow-hidden">
                      <img src={img} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      {!img && (
                        <span className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary" />
                        </span>
                      )}
                      <h3 className="text-lg font-medium">{s.name}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{s.blurb}</p>
                    <p className="text-sm font-semibold text-primary">From ${s.base_price}{s.unit === "job" ? "" : "/hr"}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-y border-border" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">How it works</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-12">Simple from booking to done</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, i) => (
              <div key={step.title} className="animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }} data-testid={`how-step-${i}`}>
                <span className="h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center mb-4">
                  <step.icon className="h-6 w-6 text-primary" />
                </span>
                <h3 className="text-lg font-medium mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="trust-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-primary rounded-3xl p-8 sm:p-14 text-white relative overflow-hidden noise-overlay">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold max-w-xl mb-4">
              Local, reliable and easy to deal with
            </h2>
            <p className="text-white/80 max-w-lg mb-8">
              One call or one booking and your job is on our schedule. You get confirmations, reminders and a clear price up front.
            </p>
            <ul className="space-y-3 mb-10">
              {["Instant booking confirmation", "Reminder the day before your job", "Clear estimates, no surprises", "Cooma & Snowy Monaro local"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-harvest shrink-0" /> {t}
                </li>
              ))}
            </ul>
            <Link to="/book" data-testid="trust-book-button">
              <Button size="lg" className="rounded-full bg-white text-primary hover:bg-secondary px-8 transition-colors">
                Book Your Job Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-t border-border" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Local &amp; owner-operated</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold mb-4">Reliable. Affordable. No job too small.</h2>
            <p className="text-muted-foreground mb-8 max-w-lg leading-relaxed">
              Blake&apos;s Mowing &amp; Odd Jobs is based in Cooma and services the surrounding Snowy Monaro towns.
              Call or message today for a free quote — or book online in two minutes.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {["Cooma NSW", "Bredbo NSW", "Berridale NSW"].map((town) => (
                <span key={town} className="flex items-center gap-1.5 bg-secondary rounded-full px-4 py-2 text-sm font-medium" data-testid={`service-area-${town.split(" ")[0].toLowerCase()}`}>
                  <MapPin className="h-4 w-4 text-primary" /> {town}
                </span>
              ))}
            </div>
            <a href={`tel:${phoneTel}`} data-testid="about-call-button">
              <Button className="rounded-full bg-accent hover:bg-[#B85827] text-white transition-colors">
                <Phone className="mr-2 h-4 w-4" /> Call for a free quote
              </Button>
            </a>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="/brand/flyer-1.jpg" alt="Blake's Mowing and Odd Jobs truck and trailer on a job"
              className="col-span-2 w-full h-56 object-cover rounded-2xl border border-border" data-testid="about-flyer-1" />
            <img src="/brand/flyer-2.jpg" alt="Blake's Mowing and Odd Jobs services flyer"
              className="col-span-2 sm:col-span-1 w-full h-72 object-cover object-top rounded-2xl border border-border" data-testid="about-flyer-2" />
            <img src="/brand/logo.jpg" alt="Blake's Mowing and Odd Jobs logo"
              className="col-span-2 sm:col-span-1 w-full h-72 object-cover rounded-2xl border border-border" data-testid="about-logo" />
          </div>
        </div>
      </section>

      <Reviews />

      <footer className="border-t border-border py-12" data-testid="public-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <img src="/brand/logo.jpg" alt="Blake's Mowing and Odd Jobs logo" className="h-9 w-9 rounded-full object-cover" />
              <span className="font-display font-bold">Blake&apos;s Mowing &amp; Odd Jobs</span>
            </div>
            <p className="text-sm text-muted-foreground">Servicing Cooma, Bredbo &amp; Berridale NSW</p>
          </div>
          <div className="text-sm">
            <a href={`tel:${phoneTel}`} className="flex items-center gap-2 font-semibold text-primary hover:text-[#21421D] transition-colors" data-testid="footer-call-link">
              <Phone className="h-4 w-4" /> {phoneDisplay}
            </a>
            <a href="mailto:bd6512949@gmail.com" className="flex items-center gap-2 mt-3 text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-email-link">
              <Mail className="h-4 w-4" /> bd6512949@gmail.com
            </a>
            <Link to="/login" className="block mt-3 text-muted-foreground hover:text-foreground transition-colors" data-testid="footer-staff-login-link">
              Staff login
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
